import { useEffect } from "react";
import { cleanDoubleSlashes } from "ufo";

type DeferredGtmLoaderProps = {
  gtmId?: string;
  isGtmProxy: boolean;
  metricsService?: string;
};

const DEFERRED_GTM_DELAY_MS = 8_000;
const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart"] as const;
const SCRIPT_ID = "deferred-gtm-loader";

const ensureDataLayer = (): DataLayer => {
  window.dataLayer = (window.dataLayer ?? []) as DataLayer;
  return window.dataLayer;
};

// prettier-fix: параметры функции перенесены на новые строки
const appendScript = (
  src: string,
  attributes?: Record<string, string>,
): void => {
  if (document.getElementById(SCRIPT_ID)) return;

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = src;

  for (const [name, value] of Object.entries(attributes ?? {})) {
    script.setAttribute(name, value);
  }

  document.head.appendChild(script);
};

const loadGtm = ({
  gtmId,
  isGtmProxy,
  metricsService,
}: {
  gtmId: string;
  isGtmProxy: boolean;
  metricsService: string;
}): void => {
  const dataLayer = ensureDataLayer();

  if (isGtmProxy) {
    appendScript(cleanDoubleSlashes(`${metricsService}/metrics/loader.js`), {
      "data-gtm-id": gtmId.replace("GTM-", ""),
    });
    return;
  }

  dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  appendScript(
    `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`,
  );
};

export const DeferredGtmLoader = ({
  gtmId,
  isGtmProxy,
  metricsService,
}: DeferredGtmLoaderProps): null => {
  useEffect(() => {
    ensureDataLayer();

    if (!gtmId || (isGtmProxy && !metricsService)) return;
    const resolvedGtmId = gtmId;
    const resolvedMetricsService = metricsService ?? "";

    let loaded = false;
    let timeoutId: number | undefined;

    function load(): void {
      if (loaded) return;
      loaded = true;

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      cleanupInteractionListeners();

      loadGtm({
        gtmId: resolvedGtmId,
        isGtmProxy,
        metricsService: resolvedMetricsService,
      });
    }

    function cleanupInteractionListeners(): void {
      for (const eventName of INTERACTION_EVENTS) {
        window.removeEventListener(eventName, load, true);
      }
    }

    function scheduleAfterLoad(): void {
      if (loaded || timeoutId) return;
      timeoutId = window.setTimeout(load, DEFERRED_GTM_DELAY_MS);
    }

    for (const eventName of INTERACTION_EVENTS) {
      window.addEventListener(eventName, load, true);
    }

    if (document.readyState === "complete") {
      scheduleAfterLoad();
    } else {
      window.addEventListener("load", scheduleAfterLoad, { once: true });
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      cleanupInteractionListeners();
      window.removeEventListener("load", scheduleAfterLoad);
    };
  }, [gtmId, isGtmProxy, metricsService]);

  return null;
};
